import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import getAddEditDutyCodeSchema from '../utils/addPersonalizedDutyCodeSchema';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import OverrideDutyCode from 'components/shared/DutyCodesGrid/AddPersonalizedDutyCode/components/OverrideDutyCode';
import DutyCodeBaseFields from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import { LoadingOverlay, showSnackbar } from 'components/shared/index';

import { Box } from '@mui/material';
import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';
import { IconTooltip } from 'components/shared/IconTooltip';
import Actions from '../../Actions';

import type { DutyCode, DutyHourType } from 'generated';
import type { InferType } from 'yup';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  onSave: () => void;
  organizationalUnitId?: number;
};

export type DutyCodePeriodForm = {
  id: number;
  dutyHourTypeId: number;
  startTime: Date;
  endTime: Date;
  dutyHourType: DutyHourType;
};

export type DutyCodeForm = InferType<
  ReturnType<typeof getAddEditDutyCodeSchema>
>;

const defaultFormValues: DutyCodeForm = {
  displayDutyCode: '',
  colorCode: '',
  overrideDutyCodeId: -1
};

const AddPersonalizedDutyCode = ({
  isOpen,
  handleClose,
  onSave,
  organizationalUnitId
}: Props) => {
  'use memo';

  const { t } = useTranslation('common');
  const methods = useForm<DutyCodeForm>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(getAddEditDutyCodeSchema())
  });

  const { control, handleSubmit, reset, setValue } = methods;
  const { mutate: createDutyCode, isLoading: isCreating } =
    mutations.useCreateDutyCode();
  const QueryClient = useQueryClient();

  function handleReset() {
    reset(defaultFormValues);
  }

  function onSubmit(data: DutyCodeForm) {
    if (data.overrideDutyCodeId && data.overrideDutyCodeId > 0) {
      createDutyCode(
        {
          dutyCodeId: data.overrideDutyCodeId,
          code: data.displayDutyCode,
          colorCode: data.colorCode,
          organizationalUnitId: organizationalUnitId
        },
        {
          onSuccess: () => {
            QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
            onSave();
            handleReset();
          },
          onError: (error: any) => {
            const message = t(
              error?.response?.data?.error?.message || 'somethingWentWrong'
            );
            showSnackbar({
              severity: 'error',
              message: message
            });
          }
        }
      );
    }
  }

  function onOverrideDutyCodeSelect(dutyCode?: DutyCode) {
    setValue('overrideDutyCodeId', dutyCode?.dutyCodeId || -1);
    setValue('displayDutyCode', dutyCode?.dutyCode || '');
  }

  function onDialogClose() {
    handleClose();
    handleReset();
  }

  return (
    <StyledDialogMedium
      open={isOpen}
      title={t('addPersonalizedDutyCode')}
      handleClose={onDialogClose}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'}>
            <OverrideDutyCode
              control={control}
              onDutyCodeSelect={onOverrideDutyCodeSelect}
            />
            <IconTooltip
              fontSize="small"
              title={t('dutyCodeTooltip')}
              placement="top-end"
            />
          </Box>
          <DutyCodeBaseFields />
          <Actions onDialogClose={onDialogClose} />
          <LoadingOverlay loading={isCreating} />
        </form>
      </FormProvider>
    </StyledDialogMedium>
  );
};

export default AddPersonalizedDutyCode;
